import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { IAppBarNavItem } from './AppBar.models';
import { OnlineThemeType } from '../../themes/online';
import vIcon from '../../assets/v-icon.svg';
import Locked from '../../assets/locked.svg';
import chevronDownIcon from '../../assets/chevron-down-icon.svg';
import chevronUpIcon from '../../assets/chevron-up-icon.svg';
import classNames from 'classnames';

export interface INavigationProps {
  className?: string;
  navItems: Array<IAppBarNavItem>;
  activeItem: string;
  onNavItemClick: (name: string) => void;
}

export interface INavigationPropsWithTheme extends INavigationProps {
  theme: OnlineThemeType;
}

const StyledNavigation = ({ theme }: INavigationPropsWithTheme) => {
  return css`
    width: 100%;
    padding-left: 16px;
    display: flex;
    align-items: flex-end;

    @media ${theme.device.max.tabletS} {
      display: none;
    }

    > .item {
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      width: 146px;
      height: 56px;
      font-weight: 500;
      background: #fff;
      font-size: 16px;
      color: ${theme.colors.placeholder2};
      cursor: pointer;
      position: relative;

      &.desktop {
        @media ${theme.device.max.laptopMS} {
          display: none;
        }
      }

      &.mobile {
        @media ${theme.device.laptopMS} {
          display: none;
        }
      }

      &.disabled {
        cursor: not-allowed;
      }

      &.is-active {
        border-bottom: 4px solid ${theme.colors.primaryBright};
        color: ${theme.colors.primaryBright};
        background: #fff;

        > .icon {
          filter: invert(70%) sepia(96%) saturate(7463%) hue-rotate(226deg) brightness(99%) contrast(105%); //primaryBright
        }
      }

      > .more {
        position: absolute;
        color: ${theme.colors.primaryBackground};
        min-width: 196px;
        z-index: 1;
        margin-top: 10px;
        box-shadow: 0px 8px 28px -6px rgba(24, 39, 75, 0.12), 0px 18px 88px -4px rgba(24, 39, 75, 0.14);
        border-radius: 8px;
        top: 45px;
        left: 30px;
        background: #fff;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        padding: 16px 8px;
        gap: 8px;

        > .dropdown-item {
          display: flex;
          flex-direction: row;
          align-items: center;
          padding: 12px 10px;
          gap: 5px;
          width: 180px;
          height: 45px;
          font-style: normal;
          font-weight: 500;
          font-size: 16px;
          line-height: 21px;
          color: ${theme.colors.placeholder};
          border-radius: 4px;
          justify-content: space-between;

          &:hover {
            background-color: ${theme.colors.grayWhite};
          }

          &.disabled {
            cursor: not-allowed;
          }

          &.is-active {
            background-color: ${theme.colors.brightBlue};
          }

          & > .more-item {
            display: flex;
            align-items: center;
            gap: 12px;
          }
        }
      }
    }
  `;
};

export const Navigation = styled((props: INavigationProps) => {
  const [isMoreOpen, setIsMoreOpen] = useState<boolean>(false);

  const toggleMore = () => setIsMoreOpen(!isMoreOpen);

  const onItemClick = (name: string, disabled: boolean) => {
    if (disabled) return;

    setIsMoreOpen(false);
    props.onNavItemClick(name);
  };

  const collapsibleItems = useMemo(() => props.navItems.filter((item) => item.collapsible), [props.navItems]);
  const permanentItems = useMemo(() => props.navItems.filter((item) => !item.collapsible), [props.navItems]);

  return (
    <nav className={props.className}>
      <>
        {permanentItems.map((item) => (
          <div
            key={item.name}
            className={classNames('item', {
              'is-active': item.name === props.activeItem && !item.disabled,
              disabled: item.disabled,
            })}
            onClick={() => onItemClick(item.name, item.disabled)}
          >
            <img className="icon" src={item.disabled ? Locked : item.svgIconSrc} />
            {item.title}
          </div>
        ))}
      </>
      <>
        {collapsibleItems.map((item) => (
          <div
            key={item.name}
            className={classNames('item', 'desktop', {
              'is-active': item.name === props.activeItem && !item.disabled,
              disabled: item.disabled,
            })}
            onClick={() => onItemClick(item.name, item.disabled)}
            data-at={`appbar-nav-item-${item.name}`}
          >
            <img className="icon" src={item.disabled ? Locked : item.svgIconSrc} />
            {item.title}
          </div>
        ))}
      </>
      <div className={classNames('item', 'mobile', { 'is-active': isMoreOpen })} onClick={toggleMore}>
        More
        <img src={isMoreOpen ? chevronUpIcon : chevronDownIcon} />
        {isMoreOpen && (
          <div className="more">
            {collapsibleItems.map((item) => (
              <div
                key={item.name}
                className={classNames('dropdown-item', {
                  'is-active': item.name === props.activeItem && !item.disabled,
                  disabled: item.disabled,
                })}
                onClick={() => onItemClick(item.name, item.disabled)}
                data-at={`appbar-nav-item-${item.name}`}
              >
                <span className="more-item">
                  {item.disabled && <img className="locked" src={Locked} />}
                  {item.title}
                </span>
                {item.name === props.activeItem && !item.disabled && <img src={vIcon} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
})`
  ${StyledNavigation}
`;
